import ErrorDialog from "@/components/ErrorDialog"
import { connectWallet, shortenAddress } from "@/lib/chain"
import { TOKEN_LOGO } from "@/lib/brand"
import { useMemo, useState } from "react"

const KLIPO_PER_ETH = 12500

type Token = "ETH" | "KLIPO"

function quote(fromToken: Token, toToken: Token, amount: number) {
  if (!amount || fromToken === toToken) return 0
  if (fromToken === "ETH" && toToken === "KLIPO") return amount * KLIPO_PER_ETH
  return amount / KLIPO_PER_ETH
}

function formatAmount(value: number) {
  if (!value) return "0"
  if (value >= 1000) return value.toLocaleString(undefined, { maximumFractionDigits: 2 })
  return value.toLocaleString(undefined, { maximumFractionDigits: 6 })
}

export default function SwapPage() {
  const [buyToken, setBuyToken] = useState<Token>("KLIPO")
  const [sellToken, setSellToken] = useState<Token>("ETH")
  const [buyAmount, setBuyAmount] = useState("")
  const [sellAmount, setSellAmount] = useState("")
  const [wallet, setWallet] = useState("")
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState("")
  const [note, setNote] = useState("")

  const rateLabel = useMemo(() => {
    if (sellToken === buyToken) return "Choose two different tokens"
    if (sellToken === "ETH") return `1 ETH = ${KLIPO_PER_ETH.toLocaleString()} KLIPO`
    return `1 KLIPO = ${(1 / KLIPO_PER_ETH).toFixed(8)} ETH`
  }, [buyToken, sellToken])

  const setSell = (token: Token) => {
    setSellToken(token)
    const nextBuy = token === "ETH" ? "KLIPO" : "ETH"
    setBuyToken(nextBuy)
    const amount = Number(sellAmount) || 0
    setBuyAmount(amount ? String(quote(token, nextBuy, amount)) : "")
  }

  const setBuy = (token: Token) => {
    setBuyToken(token)
    const nextSell = token === "ETH" ? "KLIPO" : "ETH"
    setSellToken(nextSell)
    const amount = Number(buyAmount) || 0
    setSellAmount(amount ? String(quote(nextSell, token, amount)) : "")
  }

  const onSellAmount = (value: string) => {
    setSellAmount(value)
    const amount = Number(value) || 0
    setBuyAmount(amount ? String(quote(sellToken, buyToken, amount)) : "")
  }

  const onBuyAmount = (value: string) => {
    setBuyAmount(value)
    const amount = Number(value) || 0
    setSellAmount(amount ? String(quote(buyToken, sellToken, amount)) : "")
  }

  const handleConnect = async () => {
    setBusy(true)
    setNote("")
    try {
      const address = await connectWallet()
      setWallet(address)
    } catch (err) {
      console.error("Wallet connect failed:", err)
      setError((err as Error)?.message || "Could not connect wallet.")
    } finally {
      setBusy(false)
    }
  }

  const handleSwap = async () => {
    const pay = Number(sellAmount) || 0
    const receive = Number(buyAmount) || 0
    if (sellToken === buyToken) {
      setError("Pick ETH on one side and KLIPO on the other.")
      return
    }
    if (pay <= 0 || receive <= 0) {
      setError("Enter an amount to swap.")
      return
    }
    if (!wallet) {
      await handleConnect()
      return
    }
    setBusy(true)
    setNote("")
    try {
      await connectWallet()
      setNote(`Quote ready: ${formatAmount(pay)} $${sellToken} → ${formatAmount(receive)} $${buyToken} on Robinhood Chain. $KLIPO pool routing is not live yet — this quote is locked at 12,500 KLIPO per ETH.`)
    } catch (err) {
      console.error("Swap failed:", err)
      setError((err as Error)?.message || "Could not start this swap.")
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="px-4 py-5">
      <p className="text-[11px] uppercase tracking-[0.22em] text-[#6E6E6E]">KLIPO DEX</p>
      <h1 className="mt-1 font-[Syne] text-2xl font-bold">Swap</h1>
      <p className="mt-1 text-sm text-[#A8A8A8]">Buy and sell $ETH and $KLIPO on Robinhood Chain.</p>

      <div className="relative mt-6">
        <TokenPanel
          title="Buy"
          token={buyToken}
          amount={buyAmount}
          onToken={setBuy}
          onAmount={onBuyAmount}
        />

        <div className="relative z-10 -my-5 flex justify-center">
          <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-full border border-[#1F1F1F] bg-black shadow-[0_0_24px_rgba(0,255,102,0.3)]">
            <img src={TOKEN_LOGO} alt="" className="h-full w-full object-cover" />
          </div>
        </div>

        <TokenPanel
          title="Sell"
          token={sellToken}
          amount={sellAmount}
          onToken={setSell}
          onAmount={onSellAmount}
        />
      </div>

      <div className="mt-4 rounded-2xl border border-[#1F1F1F] bg-[#0B0B0B] px-4 py-3 text-sm">
        <div className="flex items-center justify-between text-[#A8A8A8]">
          <span>Rate</span>
          <span className="text-white">{rateLabel}</span>
        </div>
        <div className="mt-2 flex items-center justify-between text-[#A8A8A8]">
          <span>Network</span>
          <span className="text-[#00C805]">Robinhood Chain</span>
        </div>
        <div className="mt-2 flex items-center justify-between text-[#A8A8A8]">
          <span>Wallet</span>
          <span className="text-white">{wallet ? shortenAddress(wallet) : "Not connected"}</span>
        </div>
      </div>

      {note ? (
        <p className="mt-3 rounded-xl border border-[#00C805]/40 bg-[#141414] px-4 py-3 text-sm text-[#00C805]">
          {note}
        </p>
      ) : null}

      <button
        type="button"
        disabled={busy}
        onClick={wallet ? handleSwap : handleConnect}
        className="mt-5 w-full rounded-xl bg-[#00C805] py-3 text-sm font-semibold text-black disabled:opacity-60"
      >
        {busy ? "Working..." : wallet ? `Swap ${sellToken} for ${buyToken}` : "Connect wallet"}
      </button>

      {error ? <ErrorDialog message={error} onClose={() => setError("")} /> : null}
    </div>
  )
}

function TokenPanel({
  title,
  token,
  amount,
  onToken,
  onAmount,
}: {
  title: string
  token: Token
  amount: string
  onToken: (token: Token) => void
  onAmount: (value: string) => void
}) {
  return (
    <section className="rounded-2xl border border-[#1F1F1F] bg-[#0B0B0B] p-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold">{title}</h2>
        <div className="grid grid-cols-2 gap-1 rounded-full bg-[#141414] p-1">
          {(["ETH", "KLIPO"] as Token[]).map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => onToken(item)}
              className={`rounded-full px-3 py-1 text-xs ${token === item ? "bg-[#00C805] font-semibold text-black" : "text-[#A8A8A8]"}`}
            >
              ${item}
            </button>
          ))}
        </div>
      </div>
      <input
        value={amount}
        onChange={(event) => onAmount(event.target.value)}
        inputMode="decimal"
        placeholder="0.00"
        className="mt-4 w-full bg-transparent text-3xl font-semibold outline-none placeholder:text-[#6E6E6E]"
      />
      <p className="mt-1 text-xs text-[#6E6E6E]">{title === "Buy" ? "You receive" : "You pay"} {token}</p>
    </section>
  )
}
